'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "inputRef", "styled", "error"];
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import classNames from 'classnames';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import { USE_CLASSES } from './InputConstants';
import refObject from '../utils/propTypes/refObject';
import { FieldsetContext } from '../context/FieldsetContext';
import styled, { css } from 'styled-components';
import { remCalc } from '../core/Style';
import { rgba } from '../core/Color';
import { setInputMetrics, setInputAppearance, setUiTransition, uiFocus, setFontSize, getPlaceholderStyles } from '../utils/Styles';
import { INPUT_DEFAULT_HEIGHT, INPUT_DEFAULT_PADDING_Y, INPUT_DEFAULT_PADDING_X, INPUT_SM_FONT_SIZE, INPUT_SM_HEIGHT, FOCUS_RING_BORDER_ALPHA, TEXT_AREA_CODE_HEIGHT } from 'HubStyleTokens/sizes';
import { WEB_FONT_FAMILY, WEB_FONT_REGULAR_WEIGHT, CODE_FONT_FAMILY, CODE_FALLBACK_FONT_STACK, CODE_FONT_WEIGHT } from 'HubStyleTokens/misc';
import { FORM_CONTROL_BORDER_COLOR, FORM_CONTROL_READONLY_BACKGROUND_COLOR, FORM_CONTROL_READONLY_BORDER_COLOR, FORM_PLACEHOLDER_COLOR, FORM_DISABLED_BACKGROUND_COLOR, FORM_DISABLED_COLOR, CODE_FOREGROUND_COLOR } from 'HubStyleTokens/theme';
import { CANDY_APPLE, OLAF, FOCUS_RING_BASE, BATTLESHIP, EERIE } from 'HubStyleTokens/colors';
import { jsx as _jsx } from "react/jsx-runtime";
const placeholderStyles = getPlaceholderStyles(css(["color:", ";"], ({
  $unstyled,
  use
}) => {
  if ($unstyled && use === 'on-dark') {
    return FORM_PLACEHOLDER_COLOR;
  } else if (use === 'on-dark') {
    return BATTLESHIP;
  }
  return EERIE;
}));
const StyledTextArea = styled.textarea.withConfig({
  displayName: "UITextArea__StyledTextArea",
  componentId: "d2ozwu-0"
})(["color:", ";", ";", ";", ";", ";height:auto;.private-moveable-group .private-form__set:first-of-type &{padding-left:26px;}", " ", " ", " font-family:", ";font-weight:", ";min-height:", ";resize:", ";", ";", ";", ";&:focus{border-color:", ";outline:0;", ";", "}&:disabled{background-color:", ";border-color:", ";box-shadow:none !important;color:", ";cursor:not-allowed;}&[readonly]{&:not([aria-expanded='true']){&:not(.private-form__control--inline){background-color:", ";border-color:", ";box-shadow:none;}}}"], ({
  $isCode
}) => $isCode ? CODE_FOREGROUND_COLOR : FORM_PLACEHOLDER_COLOR, placeholderStyles, setInputAppearance(), setInputMetrics(parseInt(INPUT_DEFAULT_PADDING_Y, 10) - 1, INPUT_DEFAULT_PADDING_X), setUiTransition(), ({
  $unstyled,
  use
}) => {
  if ($unstyled) {
    return css(["background-color:transparent !important;"]);
  } else if (use === 'on-dark') {
    return css(["background-color:", ";"], OLAF);
  }
  return null;
}, ({
  error,
  $isCode,
  $unstyled
}) => {
  if (error) {
    return css(["border-color:", " !important;"], CANDY_APPLE);
  } else if ($isCode) {
    return css(["border-color:", " !important;"], FORM_CONTROL_BORDER_COLOR);
  } else if ($unstyled) {
    return css(["border-color:transparent !important;"]);
  }
  return null;
}, ({
  error,
  $unstyled
}) => {
  if (error) {
    return css(["box-shadow:0 0 0 1px ", " !important;"], CANDY_APPLE);
  } else if ($unstyled) {
    return css(["box-shadow:none !important;"]);
  }
  return null;
}, ({
  $isCode
}) => $isCode ? `${CODE_FONT_FAMILY}, ${CODE_FALLBACK_FONT_STACK} !important` : `${WEB_FONT_FAMILY} !important`, ({
  $isCode
}) => $isCode ? `${CODE_FONT_WEIGHT} !important` : `${WEB_FONT_REGULAR_WEIGHT} !important`, ({
  $isCode,
  rows
}) => {
  if ($isCode) {
    return remCalc(TEXT_AREA_CODE_HEIGHT);
  } else if (rows) {
    return '0';
  }
  return remCalc(INPUT_DEFAULT_HEIGHT);
}, ({
  resize
}) => !resize && 'none' || resize === 'horizontal' && 'horizontal' || resize === 'vertical' && 'vertical', ({
  resize
}) => !resize && css(["display:inline-block;"]), ({
  cols
}) => cols > 0 && css(["width:auto;"]), ({
  $fieldsize
}) => $fieldsize === 'small' && css(["", ";height:", ";padding-bottom:", ";padding-top:", ";"], setFontSize(INPUT_SM_FONT_SIZE), remCalc(INPUT_SM_HEIGHT), remCalc(4), remCalc(4)), rgba(FOCUS_RING_BASE, FOCUS_RING_BORDER_ALPHA), ({
  readOnly,
  $unstyled
}) => !readOnly && !$unstyled && uiFocus, ({
  error,
  $unstyled
}) => error && !$unstyled && css(["box-shadow:0 0 1px 2px ", " !important;"], CANDY_APPLE), FORM_DISABLED_BACKGROUND_COLOR, FORM_DISABLED_BACKGROUND_COLOR, FORM_DISABLED_COLOR, FORM_CONTROL_READONLY_BACKGROUND_COLOR, FORM_CONTROL_READONLY_BORDER_COLOR);
class UITextArea extends PureComponent {
  render() {
    const _this$props = this.props,
      {
        className,
        inputRef,
        styled: styledProp,
        error
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      size: fieldSize
    } = this.context;
    const computedClassName = classNames('form-control', className, error && 'private-form__control--error');
    const renderedTextArea = /*#__PURE__*/_jsx(StyledTextArea, Object.assign({}, rest, {
      className: computedClassName,
      ref: inputRef,
      $unstyled: !styledProp,
      $fieldsize: fieldSize
    }));
    return renderedTextArea;
  }
}
UITextArea.displayName = 'UITextArea';
UITextArea.contextType = FieldsetContext;
UITextArea.defaultProps = {
  error: false,
  resize: 'vertical',
  styled: true,
  value: ''
};
UITextArea.propTypes = {
  cols: PropTypes.number,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  inputRef: refObject,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  resize: PropTypes.oneOf(['vertical', 'horizontal', true, false]),
  rows: PropTypes.number,
  styled: PropTypes.bool.isRequired,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES)),
  value: PropTypes.string
};
export default ShareInput(Controllable(UITextArea));